import { HHTextField } from '@hinge-health/react-component-library';
import { useState } from 'react';
import { useDynamicDrawerContext } from '../../../../context/dynamic-drawer.context';
import { useSnackbarContext } from '../../../../context/snackbar.context';
import {
  GetSubmissionsDocument,
  useBulkUpdateSubmissionsMutation,
} from '../../../../types';
import {
  ActionTypeDisplayText,
  GridLevelName,
} from '../../../grid-components/grid-types';
import { PanelAlert } from '../../../grid-components/panel-alert';
import { PanelLayout } from '../../../grid-components/panel-layout';
import { PanelTitle } from '../../../grid-components/panel-title';
import { updateNetSuiteIdInvalid } from '../../../grid-components/panel-validation';
import { SubmissionTypeDataList } from '../bill-management-types';
import { SubmissionPanelProps } from '../panel-types';
import { SelectedSubmissionsGrid } from './selected-submissions-grid';

export const UpdateNetsuiteIdPanel = ({
  selectedRows,
  onClearSelections,
}: SubmissionPanelProps): JSX.Element => {
  const { setIsOpen, setTitle } = useDynamicDrawerContext();
  const { setSnackbarState } = useSnackbarContext();
  const [note, setNote] = useState<string>('');
  const [panelSelectedRows, setPanelSelectedRows] =
    useState<SubmissionTypeDataList>(selectedRows);
  const [netsuiteId, setNetsuiteId] = useState<string>('');
  const [bulkUpdateSubmissions, { loading }] =
    useBulkUpdateSubmissionsMutation();

  const handleUpdateNetsuiteId = async (): Promise<void> => {
    try {
      const result = await bulkUpdateSubmissions({
        variables: {
          bulkUpdateSubmissionsInput: {
            ids: panelSelectedRows
              .map(row => row.submissionId)
              .filter(id => id != null),
            netsuiteId,
            note,
          },
        },
        refetchQueries: [GetSubmissionsDocument],
      });
      setSnackbarState(
        `Successfully updated Netsuite IDs for ${result.data?.bulkUpdateSubmissions?.ids.length ?? 0} submissions`,
        'success',
        5000,
      );
      onClearSelections();
      setTimeout(() => setIsOpen(false), 500);
    } catch (e) {
      setSnackbarState(
        `Failed to update Netsuite IDs with error: ${e instanceof Error ? e.message : 'Unknown error'}`,
        'error',
        5000,
      );
    }
  };

  const handleSelectionChanged = (
    selectedGridRows: SubmissionTypeDataList,
  ): void => {
    setPanelSelectedRows(selectedGridRows);
    setTitle(
      <PanelTitle
        actionType={ActionTypeDisplayText.UPDATE_NETSUITE_ID}
        rowCount={selectedGridRows.length}
        gridLevel={GridLevelName.SUBMISSION}
      />,
    );
  };

  return (
    <PanelLayout
      selectedGrid={
        <SelectedSubmissionsGrid
          selectedRows={panelSelectedRows}
          setPanelSelectedRows={handleSelectionChanged}
        />
      }
      additionalInputs={
        <HHTextField
          label="Netsuite ID *"
          value={netsuiteId}
          onChange={(e): void => setNetsuiteId(e.target.value)}
          variant="outlined"
          hhVariant="standard"
        />
      }
      alert={<PanelAlert drawerMode="update-netsuite-id" />}
      note={note}
      setNote={setNote}
      setIsOpen={setIsOpen}
      isSubmitting={loading}
      isInvalid={updateNetSuiteIdInvalid(note, netsuiteId)}
      actionLabel="Update Netsuite ID"
      handleSubmit={handleUpdateNetsuiteId}
    />
  );
};
